import * as React from 'react';
import { ReactNode, RefObject, useContext, useState } from 'react';
import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { ValidationResult } from '../../../../util/validation/interfaces';
import { ToastContext, ToastContextType } from '../../../../util/providers/ToastProvider';
import { ApiResponse, SolrDocument } from '../../../../api/core';

interface Props<T, U> {
  load: (params: { q: string }) => Promise<ApiResponse<SolrDocument[]>>;
  map: (data: SolrDocument) => U;
  value: T[];
  onChange?: (value: U[]) => void;
  itemTemplate: (item: U) => ReactNode;
  selectedItemTemplate: (item: T) => ReactNode;
  multiple: boolean;
  disabled?: boolean;
  required?: boolean;
  validation?: ValidationResult;
  inputId?: string;
  autoFocus?: boolean;
  border?: 'border-noround' | 'border-noround-right';
  tooltip?: string;
  tooltipOptions?: any;
  className?: string;
  ref?: RefObject<any>;
}

export function Suggester<T, U>(props: Props<T, U>): ReactNode {
  const toast: ToastContextType = useContext(ToastContext);
  const [suggestions, setSuggestions] = useState<U[]>([]);
  const validation: ValidationResult = props.validation ?? {valid: true};

  const loadSuggestions = async (q: string): Promise<void> => {
    const result: U[] = await props
      .load({q})
      .then((apiResponse: ApiResponse<SolrDocument[]>): Promise<SolrDocument[]> => {
        if (apiResponse.raw.ok) {
          return apiResponse.value();
        }
        throw apiResponse;
      })
      .then((solrDocuments: SolrDocument[]): U[] => solrDocuments.map(props.map))
      .catch((e: any): U[] => {
        toast.error(e, 'message.loading.failed');
        return [];
      });
    setSuggestions(result);
  };

  return (
    <div className={['flex w-full min-w-0 p-fluid', props.className].filter(Boolean).join(' ')}>
      <AutoComplete
        inputId={props.inputId}
        ref={props.ref}
        pt={{
          root: {className: 'w-full min-w-0'},
          inputToken: {className: `${!props.multiple && props.value.length > 0 ? 'hidden' : ''}`},
          container: {className: props.border ? `w-full min-w-0 overflow-hidden p-2 ${props.border}` : 'w-full min-w-0 overflow-hidden p-2'},
          token: {className: 'flex max-w-full min-w-0 overflow-hidden surface-200 border-round'},
          tokenLabel: {className: 'flex-1 min-w-0 overflow-hidden text-overflow-ellipsis white-space-nowrap'},
          removeTokenIcon: {className: 'flex-shrink-0 w-2rem text-base'},
        }}
        value={props.value as any[]}
        disabled={props.disabled}
        invalid={!validation.valid}
        required={props.required}
        itemTemplate={props.itemTemplate}
        selectedItemTemplate={props.selectedItemTemplate}
        completeMethod={(event: AutoCompleteCompleteEvent): Promise<void> => loadSuggestions(event.query)}
        onChange={(event: AutoCompleteChangeEvent): void => {
          if (props.multiple) {
            props.onChange?.(event.value as any[]);
          } else {
            props.onChange?.((event.value as any[]).length > 0 ? [(event.value as any[]).pop()] : []);
          }
        }}
        suggestions={suggestions}
        multiple={true}
        selectionLimit={props.multiple ? undefined : 1}
        autoFocus={props.autoFocus}
        tooltip={props.tooltip}
        tooltipOptions={props.tooltipOptions}
      />
    </div>
  );
}
